import React from "react";
import { useTranslation } from "react-i18next";
import { Coins } from "lucide-react";
import { bizCoinsApi, type BizWalletData } from "../lib/api";

/**
 * Compteur BizCoins permanent en barre (refonte Home — L1, façon Duolingo).
 * Visible sur TOUS les écrans (mobile + desktop). Reste affiché même en mode
 * « virtuel » (score de progression). Se masque si le porte-monnaie est
 * inaccessible (fonctionnalité non activée pour le compte → 403).
 */
export function BizCoinsBadge({ onNavigate }: { onNavigate?: (page: string) => void }) {
  const { t } = useTranslation();
  const [wallet, setWallet] = React.useState<BizWalletData | null>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);

  React.useEffect(() => {
    bizCoinsApi.getWallet().then(setWallet).catch(() => setWallet(null));
  }, []);

  if (!wallet) return null;

  const levelLabel = t(`bizCoins.level.${wallet.levelKey}`, wallet.levelKey);

  return (
    <button
      type="button"
      onClick={() => onNavigate?.("rewards")}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      // `min-h-[32px]` : le badge tombait à 30 px, juste sous le seuil de
      // confort tactile que `play-responsive.spec.ts` fait respecter à l'accueil.
      className="relative inline-flex min-h-[32px] items-center gap-1 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
      title={t("bizCoins.badgeTitle", { count: wallet.balance })}
      aria-label={t("bizCoins.badgeTitle", { count: wallet.balance })}
    >
      <Coins className="h-4 w-4 text-amber-500" />
      <span className="text-sm font-bold text-amber-700 tabular-nums">{wallet.balance.toLocaleString()}</span>
      {showTooltip && (
        <div className="absolute top-full mt-2 right-0 z-30 w-52 px-3 py-2 bg-white rounded-lg shadow-lg border text-xs text-left">
          <div className="font-semibold mb-1" style={{ color: "#384872" }}>{t("bizCoins.title")}</div>
          <div className="text-muted-foreground">{t("bizCoins.tooltipBalance", { count: wallet.balance })}</div>
          <div className="text-muted-foreground mt-0.5">{t("bizCoins.tooltipLevel", { level: levelLabel })}</div>
        </div>
      )}
    </button>
  );
}
